import React, { useEffect, useState } from 'react';
import {
  getMalaysianDateTime,
  getTodayDateTimeUTC,
  isUserFromAmericanContinent,
  sendFormSubmissionEmail,
  updateEnterpriseLeadsInGoogleSheet,
  validateEmail,
} from 'src/utils/functions';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2, SIDEBAR_COLOR } from 'src/utils/colors';
import postOnSlack from 'src/core/postOnSlack';
import { User } from 'src/interfaces/iuser';
import {
  CALENDARY_URL_FOR_AMERICAN,
  CALENDARY_URL_FOR_NON_AMERICAN,
  SAMPLE_DATA_URL,
  SCALE_CTA,
} from 'src/utils/const';
import { useSelector } from 'react-redux';

const DemoBookingForm = ({
  matches,
  user,
  showDemoBookingForm,
  closeBookingForm,
  postSubmitRedirection,
  headingFor = 'demo',
}: {
  matches: boolean;
  user: User;
  showDemoBookingForm: boolean;
  closeBookingForm: any;
  postSubmitRedirection?: any;
  headingFor?: string;
}) => {
  const search = useSelector(state => (state as any).dashboard.search);

  const [email, setEmail] = useState<string>(user.email == 'Trial' ? '' : user.email);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [markets, setMarkets] = useState<string>(search?.data?.name);
  const [additionNote, setAdditionalNote] = useState<string>('');
  const [showEmailNotEnteredWarning, setEmailNotEnteredWarning] = useState<boolean>(false);
  const [showEnterValidEmailWarning, setEnterValidEmailWarning] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [workEmail, setWorkEmail] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [companySize, setCompanySize] = useState<any>(10);
  const [purpose, setPurpose] = useState<string>('Find best locations to invest');
  const leadFormSource = useSelector(state => (state as any).dashboard.leadFormSource);

  const headingMapper = {
    sales: 'Talk to Sales',
    demo: `${SCALE_CTA} to unlock this feature today!`,
  };

  const slackChannelMapper = {
    sales: 'C05JDGPMH8T',
    demo: 'C072095QUU9',
  };

  useEffect(() => {
    setMarkets(search?.data?.name);
  }, [search.data.name]);

  useEffect(() => {
    setEmail(user.email == 'Trial' ? '' : user.email);
  }, [user]);

  const getGoogleSheetParameters = (todaysDate, sheetName) => {
    let googleSheetParameters = `[${todaysDate}|${firstName + ' ' + lastName}|${email}|${
      purpose == 'Find best locations to invest'
        ? '[Default]Find best locations to invest'
        : purpose
    }|${markets}|${additionNote}|${leadFormSource}]`;

    if (sheetName == 'host_data') {
      googleSheetParameters = `[${todaysDate}|${firstName +
        ' ' +
        lastName}|${email}|''|''|''|''|''|${markets}|${additionNote}|${leadFormSource}]`;
    } else if (sheetName == 'listings_data') {
      googleSheetParameters = `[${todaysDate}|${firstName +
        ' ' +
        lastName}|${email}|''|''|''|${markets}|${additionNote}|${leadFormSource}]`;
    } else if (sheetName == 'demo_booking_request') {
      googleSheetParameters = `[${todaysDate}|${firstName +
        ' ' +
        lastName}|${workEmail}|${company}|''|''|${additionNote}|${leadFormSource}|${
        companySize == 100 ? '50+' : 'upto ' + companySize
      }| yes]`;
    }
    return googleSheetParameters;
  };

  const handleOnContactSalesBtn = async () => {
    if (
      (headingFor == 'demo' && (email === 'Trial' || email === '')) ||
      (headingFor == 'sales' && workEmail === '')
    ) {
      setEmailNotEnteredWarning(true);
      setEnterValidEmailWarning(false);
    } else {
      if (
        (headingFor == 'demo' && validateEmail(email)) ||
        (headingFor == 'sales' && validateEmail(workEmail))
      ) {
        setSubmitLoading(true);
        setEmailNotEnteredWarning(false);
        setEnterValidEmailWarning(false);

        if (headingFor == 'demo') {
          let message = `Scale Form Submitted \nName: ${firstName +
            ' ' +
            lastName}\nUser: ${email}\nPurpose: ${purpose}\nMarkets: ${markets}\nAdditional notes: ${additionNote}\n`;
          let sendSlackMessage = await postOnSlack(slackChannelMapper['demo'], message);
        }
        let name = firstName;
        let emailText = `<b>Hi ${name},</b> <br><br> Thanks for your interest in Airbtics. We’ll review your request and get back to you in 1-2 working days. In the mean time... <br><br> <b>API documentation: </b> <a href="https://documenter.getpostman.com/view/25155751/2s9XxtyvNn">Airbtics Estimator API Documentation</a> <br> <b>Data dictionary And Sample: </b><a href=${SAMPLE_DATA_URL}>Airbtics Data Dictionary</a><br><br> Thanks, <br>Airbtics Team`;
        let title = 'Airbtics Demo Booking Confirmation: Your Request Has Been Received ';
        await sendFormSubmissionEmail(email, encodeURIComponent(emailText), title);
        let todaysDate = getMalaysianDateTime();
        if (headingFor == 'sales') {
          await updateEnterpriseLeadsInGoogleSheet(
            'demo_booking_request',
            getGoogleSheetParameters(todaysDate, 'demo_booking_request')
          );
        } else {
          if (purpose == 'Host level data') {
            await updateEnterpriseLeadsInGoogleSheet(
              'host_data',
              getGoogleSheetParameters(todaysDate, 'host_data')
            );
          } else if (purpose == 'Property level data') {
            await updateEnterpriseLeadsInGoogleSheet(
              'listings_data',
              getGoogleSheetParameters(todaysDate, 'listings_data')
            );
          } else {
            await updateEnterpriseLeadsInGoogleSheet(
              'scale_plan_request',
              getGoogleSheetParameters(todaysDate, 'scale_plan_request')
            );
          }
        }

        setSubmitLoading(false);
        closeBookingForm();
        window.alert('Form submitted successfully');
        postSubmitRedirection &&
          setTimeout(() => {
            postSubmitRedirection();
          }, 3000);

        if (workEmail) {
          let message = `Custom Data Booking Form Submitted \nName: ${firstName +
            ' ' +
            lastName}\nUser: ${email}\nCompany name: ${company}\nSize of company: ${
            companySize == 100 ? '50+' : 'upto ' + companySize
          }\nAdditional notes: ${additionNote}\n`;
          let sendSlackMessage = await postOnSlack(slackChannelMapper['sales'], message);
          if (isUserFromAmericanContinent()) {
            window.open(CALENDARY_URL_FOR_AMERICAN, '_blank');
          } else {
            window.open(CALENDARY_URL_FOR_NON_AMERICAN, '_blank');
          }
          // Send email to Jae
          let emailText = `<b>Hi Jae,</b> <br><br> The User <b>${firstName +
            ' ' +
            lastName}</b> has Showed Interest for custom data <br><br> Work Email: ${workEmail} <br> Company: ${company} <br> Company Size: ${companySize} <br> Notes: ${additionNote} <br><br> Thanks,<br> Piyush`;
          let title = 'Qualified Lead For Custom Data ';
          await sendFormSubmissionEmail('jae@airbtics.com', encodeURIComponent(emailText), title);
        }
      } else {
        setEmailNotEnteredWarning(false);
        setEnterValidEmailWarning(true);
      }
    }
  };

  return (
    <Modal
      open={showDemoBookingForm}
      onClose={closeBookingForm}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '1700',
      }}
    >
      <Grid
        container
        item
        // xs={12}
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxHeight: matches ? '500px' : '700px',
          position: 'relative',
          width: '40%',
          minWidth: '300px',
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            height: '70px',
            padding: '20px 20px 0px 20px',
            border: 'none',
            color: COLOR_PRIMARY_1,
            fontWeight: 'bold',
            //   boxShadow: '0 4px 12px #0002',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">{headingMapper[headingFor]}</Typography>
          <CloseIcon
            sx={{ cursor: 'pointer', color: COLOR_PRIMARY_1 }}
            onClick={closeBookingForm}
          />
        </Grid>
        <Grid
          container
          item
          xs={12}
          sx={{
            maxHeight: matches
              ? '330px'
              : showEmailNotEnteredWarning || showEnterValidEmailWarning
              ? '460px'
              : '530px',
            overflow: 'auto',
          }}
        >
          <Grid
            container
            item
            xs={12}
            sx={{
              padding: '20px 40px',
            }}
          >
            <Grid container item xs={12}>
              <Grid item xs={6} sx={{ paddingRight: '5px' }}>
                <TextField
                  id="outlined-basic"
                  label="First Name"
                  variant="outlined"
                  fullWidth
                  value={firstName}
                  onChange={e => {
                    setFirstName(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={6} sx={{ paddingLeft: '5px' }}>
                <TextField
                  id="outlined-basic"
                  label="Last Name"
                  variant="outlined"
                  fullWidth
                  value={lastName}
                  onChange={e => {
                    setLastName(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
            {headingFor == 'demo' && (
              <Grid container item xs={12} sx={{ marginTop: '15px' }}>
                <TextField
                  id="outlined-basic"
                  label="Email"
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                  }}
                />
              </Grid>
            )}
            {headingFor == 'demo' && (
              <Grid container item xs={12} sx={{ marginTop: '15px' }}>
                <FormControl fullWidth>
                  <FormLabel id="demo-radio-buttons-group-label">What's your purpose?</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue={purpose}
                    name="radio-buttons-group"
                    onChange={e => {
                      setPurpose(e.target.value);
                    }}
                  >
                    <Grid container item xs={12}>
                      <Grid item xs={7}>
                        <FormControlLabel
                          value={'Find best locations to invest'}
                          control={<Radio />}
                          label="Find best locations to invest"
                        />
                      </Grid>

                      <Grid item xs={5}>
                        <FormControlLabel
                          value={'revenue'}
                          control={<Radio />}
                          label="Revenue estimation"
                        />
                      </Grid>

                      <Grid item xs={7}>
                        <FormControlLabel
                          value={'Optimize listing or nightly rate'}
                          control={<Radio />}
                          label="Optimize listing or nightly rate"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FormControlLabel
                          value={'Property level data'}
                          control={<Radio />}
                          label="Property level data"
                        />
                      </Grid>

                      <Grid item xs={7}>
                        <FormControlLabel
                          value={'Host level data'}
                          control={<Radio />}
                          label="Host level data"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FormControlLabel value={'Others'} control={<Radio />} label="Others" />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}

            {headingFor == 'sales' && (
              <Grid container item xs={12} sx={{ marginTop: '15px' }}>
                <TextField
                  id="outlined-basic"
                  label="Work Email"
                  variant="outlined"
                  fullWidth
                  value={workEmail}
                  onChange={e => {
                    setWorkEmail(e.target.value);
                  }}
                />
              </Grid>
            )}
            {workEmail && (
              <Grid container item xs={12} sx={{ marginTop: '15px' }}>
                <TextField
                  id="outlined-basic"
                  label="Company Name"
                  variant="outlined"
                  fullWidth
                  value={company}
                  onChange={e => {
                    setCompany(e.target.value);
                  }}
                />
              </Grid>
            )}

            {workEmail && (
              <Grid container item xs={12} sx={{ marginTop: '15px' }}>
                <FormControl fullWidth>
                  <FormLabel id="demo-radio-buttons-group-label">Company Size</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue={10}
                    name="radio-buttons-group"
                    onChange={e => {
                      setCompanySize(e.target.value);
                    }}
                  >
                    <FormControlLabel value={10} control={<Radio />} label="Up to 10 Persons" />
                    <FormControlLabel value={50} control={<Radio />} label="Up to 50 Persons" />
                    <FormControlLabel value={100} control={<Radio />} label="50+ Persons" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}

            {headingFor == 'demo' && purpose == 'Property level data' && (
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <TextField
                  id="outlined-basic"
                  label="Markets (List countries if you need nationwide data)"
                  variant="outlined"
                  fullWidth
                  value={markets}
                  onChange={e => {
                    setMarkets(e.target.value);
                  }}
                />
              </Grid>
            )}
            {(headingFor == 'sales' || (headingFor == 'demo' && purpose == 'Others')) && (
              <Grid item xs={12} sx={{ marginTop: '15px' }}>
                <TextField
                  id="outlined-multiline-static"
                  label="What are you looking to accomplish?"
                  variant="outlined"
                  fullWidth
                  multiline
                  value={additionNote}
                  onChange={e => {
                    setAdditionalNote(e.target.value);
                  }}
                  rows={4}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            height: showEmailNotEnteredWarning || showEnterValidEmailWarning ? '110px' : '60px',
            padding: '10px',
          }}
        >
          <Button
            sx={{
              width: '100%',
              backgroundColor: COLOR_PRIMARY_1,
              color: SIDEBAR_COLOR,
              borderRadius: '10px',
              '&:hover': { backgroundColor: COLOR_PRIMARY_1, color: SIDEBAR_COLOR },
            }}
            onClick={handleOnContactSalesBtn}
          >
            Get in Touch{' '}
            {submitLoading && (
              <CircularProgress sx={{ color: 'white', marginLeft: '4px' }} size={30} />
            )}
          </Button>
          {showEmailNotEnteredWarning && (
            <Box sx={{ margin: '10px 0px' }}>
              <Alert severity="warning" sx={{ padding: '0px 5px' }}>
                Please enter your email ID.
              </Alert>
            </Box>
          )}
          {showEnterValidEmailWarning && (
            <Box sx={{ margin: '10px 0px' }}>
              <Alert severity="error" sx={{ padding: '0px 5px' }}>
                Please enter a valid email ID.
              </Alert>
            </Box>
          )}
        </Grid>
      </Grid>
    </Modal>
  );
};

export default DemoBookingForm;
